/**
 * NumberField — a TextField for numbers, with up/down step arrows.
 *
 * The browser's own number spinner looks different in every browser and is
 * hidden until hover in most, so it is switched off and replaced by two real
 * buttons matching the design (the ⇕ at the field's end). Keyboard users keep
 * the native behaviour: ArrowUp / ArrowDown in the field still step the value,
 * which is why the buttons are left out of the tab order.
 *
 * Also carried over from legacy (ManageAccount.vue preventExponential): the
 * keys `e`, `E` and `+` are blocked — a number input accepts them, and "1e5"
 * is never what someone typing a utilisation figure meant. With `integer`,
 * the decimal point is blocked too ("Integers only").
 *
 * The value stays a STRING (what the input holds), so an empty field is ''
 * rather than 0 — "not entered" and "zero" must stay distinguishable for the
 * required-field checks.
 *
 * NO NEGATIVES when `min` is 0 or more (every number field in the app): the
 * minus key is blocked, and a pasted or dropped "-5" has its minus stripped.
 * `min` alone only stops the arrows — a native number input still accepts a
 * typed "-". Legacy does the same strip (ManageAccount.vue
 * sanitizeNonNegativeNumber).
 */
import { ChevronDownIcon, ChevronUpIcon } from '@components/common/icons';
import { TextField } from '@components/common/TextField';
import { cx } from '@lib/classNames';

import styles from './NumberField.module.css';

/** Decimal places in a step, so 0.1 + 0.2 is shown as 0.3, not 0.30000000000000004. */
function decimalsOf(step) {
  const text = String(step);
  return text.includes('.') ? text.split('.')[1].length : 0;
}

export function NumberField({
  label,
  value,
  onChange,
  min,
  max,
  step = 1,
  integer = false,
  disabled = false,
  readOnly = false,
  inputClassName,
  onKeyDown,
  ...rest
}) {
  const live = !disabled && !readOnly;

  const clamp = (n) => {
    let v = n;
    if (min != null && v < min) v = min;
    if (max != null && v > max) v = max;
    return v;
  };

  const stepBy = (direction) => {
    const current = value === '' || value == null ? null : Number(value);
    const start = Number.isFinite(current) ? current : (min ?? 0);
    // From empty, the first press lands on the start value itself.
    const next = Number.isFinite(current) ? start + direction * step : start;
    const fixed = clamp(Number(next.toFixed(decimalsOf(step))));
    onChange?.({ target: { value: String(fixed) } });
  };

  const noNegative = min != null && min >= 0;

  const blocked = ['e', 'E', '+', ...(integer ? ['.'] : []), ...(noNegative ? ['-'] : [])];

  // Paste / drop / autofill can still bring a minus in: strip it.
  const handleChange = (e) => {
    const raw = e.target.value;
    if (noNegative && typeof raw === 'string' && raw.includes('-')) {
      onChange?.({ target: { value: raw.replace(/-/g, '') } });
      return;
    }
    onChange?.(e);
  };

  return (
    <TextField
      label={label}
      type="number"
      inputMode={integer ? 'numeric' : 'decimal'}
      value={value}
      onChange={handleChange}
      min={min}
      max={max}
      step={step}
      disabled={disabled}
      readOnly={readOnly}
      inputClassName={cx(styles.noNativeSpinner, inputClassName)}
      // A caller's onKeyDown (e.g. Enter to apply) runs too, after the
      // blocking — passed through `rest` it would replace it.
      onKeyDown={(e) => {
        if (blocked.includes(e.key)) e.preventDefault();
        onKeyDown?.(e);
      }}
      endAdornment={
        <span className={styles.stepper}>
          <button
            type="button"
            tabIndex={-1}
            className={styles.stepButton}
            aria-label={`Increase ${label ?? 'value'}`}
            disabled={!live}
            onClick={() => stepBy(1)}
          >
            <ChevronUpIcon />
          </button>
          <button
            type="button"
            tabIndex={-1}
            className={styles.stepButton}
            aria-label={`Decrease ${label ?? 'value'}`}
            disabled={!live}
            onClick={() => stepBy(-1)}
          >
            <ChevronDownIcon />
          </button>
        </span>
      }
      {...rest}
    />
  );
}

export default NumberField;
